import {Button, toast} from '@cashiaApp/web-components';
import React, {useEffect, useRef, useState, useCallback} from 'react';
import {useParams} from 'react-router-dom';

import {
  airtelPayment,
  AirtelPaymentRequest,
  cardPayment,
  CardPaymentRequest,
  checkout,
  CheckoutNotificationData,
  CheckoutResponse,
  checkoutTxPolling,
  CheckoutTxStatusRequest,
  CheckoutTxStatusResponse,
  fetchQRCode,
  fetchQRCodeStatus,
  mpesaPayment,
  MpesaPaymentRequest,
  QRRequest,
  QRResponse,
  QRStatusRequest,
  TransactionResponse,
} from './api';
import {AirtelInfo} from './components/AirtelPaymentForm';
import BillingAddressForm, {
  BillingInfo,
  BillingAddressFormRef,
} from './components/BillingAddressForm';
import {CardInfo} from './components/CardPaymentForm';
import CardPaymentInline from './components/CardPaymentInline';
import {CashiaInfo} from './components/CashiaWalletForm';
import {EmailReceiptInfo} from './components/EmailReceiptForm';
import Footer from './components/Footer';
import Header from './components/Header';
import Inactive from './components/Inactive';
import {MPesaInfo} from './components/MpesaPaymentForm';
import OrderSummary from './components/OrderSummary';
import PaymentFailure from './components/PaymentFailure';
import PaymentMethodSelector, {
  PaymentMethod,
  PaymentMethodSelectorRef,
} from './components/PaymentSelector';
import PaymentSuccess from './components/PaymentSuccess';
import {PhoneReceiptInfo} from './components/PhoneReceiptForm';
import ProcessingPayment from './components/ProcessingPayment';
import ReceiptMethodSelector, {
  ReceiptMethod,
  ReceiptMethodSelectorRef,
} from './components/ReceiptSelector';
import Timer from './components/Timer';
import LoadingModal from '../../../components/common/LoadingModal';
import Spinner from '../../../components/tailwind/Spinner';
import {environment} from '../../../utils/constants';
import {cn} from '../../../utils/reusableFunctions';

const PaymentPageV2: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState<
    'form' | 'processing' | 'success' | 'failed'
  >('form');
  const {businessName, reference} = useParams();
  const [checkoutData, setCheckoutData] = useState<CheckoutResponse | null>(
    null
  );
  // const [checkoutData, setCheckoutData] = useState<SessionResponse | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [isSubmiitting, setIsSubmitting] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const [receiveReceipt, setReceiveReceipt] = useState(false);
  const [receiptMethod, setReceiptMethod] = useState<ReceiptMethod>('email');
  // Billing info - no longer need billing errors state
  const [billingInfo, setBillingInfo] = useState<BillingInfo>(() =>
    environment == 'dev'
      ? {
          country: 'United States',
          administrativeArea: 'CA',
          locality: 'san francisco',
          postalCode: '94105',
          address: '1 Market St',
        }
      : {
          country: '',
          locality: '',
          postalCode: '',
          address: '',
          administrativeArea: '',
        }
  );
  // Simplified state - forms manage their own errors now
  const [cardInfo, setCardInfo] = useState<CardInfo>(() =>
    environment == 'dev'
      ? {
          firstName: 'John',
          lastName: 'Doe',
          email: 'johndoe@gmail.com',
          cardNumber: '4000000000002503',
          expiryDate: '02/28',
          cvv: '123',
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          cardNumber: '',
          expiryDate: '',
          cvv: '',
        }
  );

  const [mpesaInfo, setMPesaInfo] = useState<MPesaInfo>({} as MPesaInfo);

  const [airtelInfo, setAirtelInfo] = useState<AirtelInfo>({} as AirtelInfo);

  const [cashiaInfo, setCashiaInfo] = useState<CashiaInfo>({} as CashiaInfo);

  const [qrCode, setQrCode] = useState<QRResponse | null>(null);

  const [emailReceiptInfo, setEmailReceiptInfo] = useState<EmailReceiptInfo>({
    email: '',
  });

  const [phoneReceiptInfo, setPhoneReceiptInfo] = useState<PhoneReceiptInfo>({
    phoneNumber: '' as PhoneReceiptInfo['phoneNumber'],
  });

  const [transactionResponse, setTransactionResponse] =
    useState<TransactionResponse | null>(null);

  // New polling-related state
  const [isPollingActive, setIsPollingActive] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Timer state and expiry tracking
  const [timeRemaining, setTimeRemaining] = useState<{
    minutes: number;
    seconds: number;
  }>({
    minutes: 0,
    seconds: 0,
  });
  const [isExpired, setIsExpired] = useState(false);

  // Refs to access form validation
  const paymentMethodRef = useRef<PaymentMethodSelectorRef>(null);
  const billingFormRef = useRef<BillingAddressFormRef>(null);
  const receiptMethodRef = useRef<ReceiptMethodSelectorRef>(null);

  // Timer effect - calculate remaining time based on createdAt
  useEffect(() => {
    if (!checkoutData?.createdAt) return;
    const calculateTimeRemaining = () => {
      const createdAt = new Date(checkoutData?.createdAt);
      const expiryTime = new Date(createdAt.getTime() + 5 * 60 * 1000); // 5 minutes after createdAt
      const now = new Date();
      const remainingMs = expiryTime.getTime() - now.getTime();
      if (remainingMs <= 0) {
        setTimeRemaining({minutes: 0, seconds: 0});
        setIsExpired(true);
        return;
      }
      const remainingMinutes = Math.floor(remainingMs / (1000 * 60));
      const remainingSeconds = Math.floor((remainingMs % (1000 * 60)) / 1000);
      setTimeRemaining({
        minutes: remainingMinutes,
        seconds: remainingSeconds,
      });
    };
    // Calculate immediately
    calculateTimeRemaining();
    // Update every second
    const timerInterval = setInterval(calculateTimeRemaining, 2000);
    return () => clearInterval(timerInterval);
  }, [checkoutData?.createdAt]);

  useEffect(() => {
    const loadCheckout = async () => {
      try {
        const response = await checkout(reference || '');
        setCheckoutData(response);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(error.message);
        } else {
          toast.error('An unknown error occurred.');
        }
      } finally {
        setLoading(false);
      }
    };
    void loadCheckout();
  }, [reference]);

  const handlePaymentCompletion = useCallback(
    (isSuccess: boolean, requestId: string, status: string) => {
      setShowCardModal(false);
      setPaymentStatus(isSuccess ? 'success' : 'failed');

      const baseUrl = isSuccess
        ? checkoutData?.successCallbackUrl
        : checkoutData?.errorCallbackUrl;

      if (baseUrl) {
        const url = `${baseUrl}?requestId=${requestId}&status=${status}`;
        toast.warning('Redirecting to website, please wait...');
        handleRedirect(url);
      }
    },
    [checkoutData?.successCallbackUrl, checkoutData?.errorCallbackUrl]
  );

  const processTxStatusResponse = useCallback(
    (response: CheckoutTxStatusResponse) => {
      const isSuccess = response?.process?.status == 'processed';
      handlePaymentCompletion(
        isSuccess,
        checkoutData?.requestId || '',
        response?.process?.status || ''
      );
    },
    [handlePaymentCompletion, checkoutData?.requestId]
  );

  // Enhanced polling effect
  useEffect(() => {
    // Don't start polling if no transaction
    if (transactionResponse == null) {
      return;
    }

    setIsPollingActive(true);

    const fetchData = async () => {
      try {
        const requestBody: CheckoutTxStatusRequest = {
          txId: transactionResponse.transaction?.id || '',
          sessionId: checkoutData?.sessionId || '',
          processId: transactionResponse?.transaction?.processId || '',
        };
        const response = await checkoutTxPolling(
          checkoutData?.sessionId || '',
          requestBody
        );
        // Check if transaction is still pending
        if (response?.process?.status == 'pending') {
          // Still pending, continue polling
          return;
        }
        // Transaction completed (success, failed, etc.)
        setIsPollingActive(false);
        // Update payment status based on response
        processTxStatusResponse(response);

        // Clear the polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
      } catch (error) {
        // setIsPollingActive(false);
        // setPaymentStatus('failed');
        // // Clear the polling interval on error
        // if (pollingIntervalRef.current) {
        //   clearInterval(pollingIntervalRef.current);
        //   pollingIntervalRef.current = null;
        // }
      }
    };

    // Fetch immediately on start
    void fetchData();

    // Set up interval polling
    pollingIntervalRef.current = setInterval(() => {
      void fetchData();
    }, 2000);

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPollingActive(false);
    };
  }, [checkoutData, processTxStatusResponse, transactionResponse]);

  useEffect(() => {
    if (!qrCode?.paymentIdentifier || reference == null) {
      return;
    }

    setIsPollingActive(true);

    const fetchData = async () => {
      try {
        const requestBody: QRStatusRequest = {
          paymentIdentifier: qrCode.paymentIdentifier,
        };
        const response = await fetchQRCodeStatus(reference, requestBody);

        const statusLower = response?.status?.toLowerCase();
        if (statusLower == 'pending') {
          return;
        }

        setIsPollingActive(false);

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        const isSuccess = statusLower == 'paid';
        const requestId = reference || '';
        const status = response?.status || 'UNKNOWN';
        handlePaymentCompletion(isSuccess, requestId, status);
      } catch (error) {
        console.error('Cashia Wallet status polling error:', error);

        setIsPollingActive(false);
        setPaymentStatus('failed');

        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        if (error instanceof Error) {
          toast.error(`Payment verification failed: ${error.message}`);
        } else {
          toast.error(
            'Unable to verify payment status. Please contact support.'
          );
        }
      }
    };

    void fetchData();

    pollingIntervalRef.current = setInterval(() => {
      void fetchData();
    }, 2000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPollingActive(false);
    };
  }, [qrCode, reference, handlePaymentCompletion]);

  // Cleanup polling when component unmounts or payment status changes
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (paymentMethod !== 'card') {
      setShowBilling(false); // hide billing info if switching from card
    }
  }, [paymentMethod]);

  const handleCardSubmit = async () => {
    // Check if session has expired
    if (isExpired || !reference) {
      toast.error('Session has expired. Please refresh the page.');
      return;
    }
    // Validate payment method using ref with proper type checking
    let isPaymentMethodValid = false;
    if (paymentMethodRef.current) {
      isPaymentMethodValid = paymentMethodRef.current.validateCurrentMethod();
    }
    // Validate billing if shown using ref with proper type checking
    let isBillingValid = true;
    if (showBilling && billingFormRef.current) {
      isBillingValid = billingFormRef.current.validateAll();
    }
    if (!isPaymentMethodValid || !isBillingValid) {
      // Validation failed, errors are already shown in the forms
      return;
    }
    setIsSubmitting(true);
    const cardPaymentRequest: CardPaymentRequest = {
      coinSerial: checkoutData?.coinSerial || '',
      amount: checkoutData?.amount || 0,
      cardInfo: {
        cardNumber: cardInfo.cardNumber.replace(/\s/g, ''),
        cardExpirationMonth: cardInfo?.expiryDate?.slice(0, 2) || '00',
        cardExpirationYear: `${
          parseInt(cardInfo?.expiryDate?.slice(3, 5) || '', 10) + 2000
        }`,
        cardCvv: cardInfo.cvv,
      },
    };
    if (showBilling) {
      cardPaymentRequest.billingInfo = {
        firstName: cardInfo.firstName,
        lastName: cardInfo.lastName,
        country: billingInfo.country,
        administrativeArea: billingInfo.administrativeArea,
        locality: billingInfo.locality,
        postalCode: billingInfo.postalCode,
        address1: billingInfo.address,
        email: cardInfo.email,
        // phoneNumber: '', // Phone number can be added if collected
      };
    }
    if (receiveReceipt) {
      // Add notification data if receipt is requested
      cardPaymentRequest.notification = handleReceiptSubmission();
    }

    try {
      const response = await cardPayment(reference, cardPaymentRequest);
      setTransactionResponse(response);
      setIsSubmitting(false);
      setPaymentStatus('processing');
      setShowCardModal(true);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      setIsSubmitting(false);
    }
  };

  const handleMpesaSubmit = async () => {
    // Check if session has expired
    if (isExpired || !reference) {
      toast.error('Session has expired. Please refresh the page.');
      return;
    }

    // Validate payment method using ref with proper type checking
    let isValid = false;
    if (paymentMethodRef.current) {
      isValid = paymentMethodRef.current.validateCurrentMethod();
    }

    if (!isValid) {
      // Validation failed, errors are already shown in the form
      return;
    }

    setIsSubmitting(true);
    const mpesaPaymentRequest: MpesaPaymentRequest = {
      createTxReq: {
        type: 'TOPUP',
        amount: checkoutData?.amount || 0,
        method: {
          gateProviderId: 'mpesa',
          way: 'EMONEY',
        },
        coin: checkoutData?.coinSerial || '',
      },
      dataReq: {
        optionName: 'default',
        fields: {
          phoneNumber: mpesaInfo.phoneNumber.trim().replace(/\+/g, ''),
        },
      },
      webhookUrl: '',
      successRedirectUrl: '',
      errorRedirectUrl: '',
    };
    if (receiveReceipt) {
      // Add notification data if receipt is requested
      mpesaPaymentRequest.notification = handleReceiptSubmission();
    }

    try {
      const response = await mpesaPayment(reference, mpesaPaymentRequest);
      setTransactionResponse(response);
      setIsSubmitting(false);
      setPaymentStatus('processing');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      setIsSubmitting(false);
    }
  };

  const handleAirtelSubmit = async () => {
    // Check if session has expired
    if (isExpired || !reference) {
      toast.error('Session has expired. Please refresh the page.');
      return;
    }

    // Validate payment method using ref with proper type checking
    let isValid = false;
    if (paymentMethodRef.current) {
      isValid = paymentMethodRef.current.validateCurrentMethod();
    }

    if (!isValid) {
      // Validation failed, errors are already shown in the form
      return;
    }

    setIsSubmitting(true);
    const airtelPaymentRequest: AirtelPaymentRequest = {
      createTxReq: {
        type: 'TOPUP',
        amount: checkoutData?.amount || 0,
        method: {
          gateProviderId: 'airtel',
          way: 'EMONEY',
        },
        coin: checkoutData?.coinSerial || '',
      },
      dataReq: {
        optionName: 'default',
        fields: {
          phoneNumber: airtelInfo.phoneNumber.trim().replace(/\+/g, ''),
          countryCode: airtelInfo.countryCode, // Ensure country code is sent if required by API
        },
      },
      webhookUrl: '',
      successRedirectUrl: '',
      errorRedirectUrl: '',
    };
    if (receiveReceipt) {
      // Add notification data if receipt is requested
      airtelPaymentRequest.notification = handleReceiptSubmission();
    }

    try {
      const response = await airtelPayment(reference, airtelPaymentRequest);
      setTransactionResponse(response);
      setIsSubmitting(false);
      setPaymentStatus('processing');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCashiaSubmit = async () => {
    // Check if session has expired
    if (isExpired || !reference) {
      toast.error('Session has expired. Please refresh the page.');
      return;
    }

    // Validate payment method using ref with proper type checking
    let isValid = false;
    if (paymentMethodRef.current) {
      isValid = paymentMethodRef.current.validateCurrentMethod();
    }

    if (!isValid) {
      // Validation failed, errors are already shown in the form
      return;
    }

    setIsSubmitting(true);
    // TODO: Implement Cashia payment API call
    // For now, just show a success message
    try {
      const body: QRRequest = {
        description: reference || '',
        phoneNumber: cashiaInfo.stikaOrPhone.trim().replace(/\+/g, ''),
      };
      const response = await fetchQRCode(reference, body);
      setQrCode(response);
      setIsSubmitting(false);
      setPaymentStatus('processing');
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('An unknown error occurred.');
      }
      setIsSubmitting(false);
    }
  };

  const handleDigitalWalletSuccess = (result: CheckoutTxStatusResponse) => {
    processTxStatusResponse(result);
  };

  const handleDigitalWalletError = (error: unknown) => {
    console.error('Digital wallet payment error:', error);
    toast.error(
      error instanceof Error ? error.message : 'Digital wallet payment failed'
    );
    setPaymentStatus('failed');
  };

  const handleSubmit = async () => {
    if (receiveReceipt) {
      // Validate receipt method using ref with proper type checking
      let isReceiptValid = false;
      if (receiptMethodRef.current) {
        isReceiptValid = receiptMethodRef.current.validateCurrentMethod();
      }
      if (!isReceiptValid) {
        // Validation failed, errors are already shown in the form
        return;
      }
    }
    if (paymentMethod === 'card') {
      await handleCardSubmit();
    } else if (paymentMethod === 'mpesa') {
      await handleMpesaSubmit();
    } else if (paymentMethod === 'airtel') {
      await handleAirtelSubmit();
    } else if (paymentMethod === 'cashia') {
      await handleCashiaSubmit();
    } else if (
      paymentMethod === 'apple-pay' ||
      paymentMethod === 'google-pay'
    ) {
      if (paymentMethodRef.current) {
        await paymentMethodRef.current.processDigitalWalletPayment();
      }
    }
  };

  const handleReceiptSubmission = () => {
    if (receiptMethod === 'email') {
      return {
        channel: 'email',
        to: emailReceiptInfo.email.trim(),
      } as CheckoutNotificationData;
    }
    if (receiptMethod === 'phone') {
      return {
        channel: 'sms',
        to: phoneReceiptInfo.phoneNumber.trim(),
      } as CheckoutNotificationData;
    }
  };

  const handleRedirect = (url: string) => {
    setTimeout(() => {
      window.location.href = `${url || ''}`;
    }, 5000);
  };

  if (loading) {
    return <LoadingModal open />;
  }

  if (checkoutData === null || isExpired) {
    return <Inactive />;
  }

  return (
    <div className="lg:flex min-h-screen  flex flex-col lg:flex-row">
      <div
        className={`lg:w-1/2 bg-transparent lg:bg-surfaceGrey lg:px-16 ${
          paymentStatus !== 'form' ? 'py-0 pt-8 lg:py-14' : 'py-14'
        }`}>
        <div className="px-3 md:px-12 mb-8">
          <Header />
        </div>

        <div className="flex flex-col lg:items-start items-center px-12">
          <div className="mb-3 md:mb-8">
            <Timer
              minutes={timeRemaining.minutes}
              seconds={timeRemaining.seconds}
            />
          </div>
          <div className="lg:w-full text-center lg:text-start">
            <OrderSummary
              checkoutData={checkoutData}
              businessName={businessName}
            />
          </div>
        </div>
      </div>
      <div className="lg:w-1/2 bg-transparent xl:px-16 lg:py-14 lg:shadow-[0px_16px_32px_4px_rgba(88,80,98,0.10)] flex-1 flex">
        {paymentStatus === 'form' && (
          <div className="flex flex-col flex-1 justify-center px-4 lg:px-16 items-start w-full">
            <>
              {showBilling && (
                <BillingAddressForm
                  ref={billingFormRef}
                  billingInfo={billingInfo}
                  setBillingInfo={setBillingInfo}
                />
              )}
              <PaymentMethodSelector
                ref={paymentMethodRef}
                checkoutData={checkoutData}
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                cardInfo={cardInfo}
                setCardInfo={setCardInfo}
                mpesaInfo={mpesaInfo}
                setMPesaInfo={setMPesaInfo}
                airtelInfo={airtelInfo}
                setAirtelInfo={setAirtelInfo}
                cashiaInfo={cashiaInfo}
                setCashiaInfo={setCashiaInfo}
                reference={reference || ''}
                onBinCheck={setShowBilling}
                onDigitalWalletSuccess={handleDigitalWalletSuccess}
                onDigitalWalletError={handleDigitalWalletError}
                onStatusChange={setPaymentStatus as (status: string) => void}
              />
              <label className="text-sm font-normal text-borderGreyHover flex gap-3 mt-2">
                <input
                  type="checkbox"
                  checked={receiveReceipt}
                  onChange={(e) => setReceiveReceipt(e.target.checked)}
                  className="accent-pink w-[18px] h-[18px]"
                />
                Would you like to receive a receipt?
              </label>
              {receiveReceipt && (
                <ReceiptMethodSelector
                  ref={receiptMethodRef}
                  receiptMethod={receiptMethod}
                  setReceiptMethod={setReceiptMethod}
                  emailReceiptInfo={emailReceiptInfo}
                  setEmailReceiptInfo={setEmailReceiptInfo}
                  phoneReceiptInfo={phoneReceiptInfo}
                  setPhoneReceiptInfo={setPhoneReceiptInfo}
                />
              )}
              <div className="w-full mb-8 mt-8">
                <Button
                  onClick={handleSubmit}
                  className={cn(
                    'p-3 w-full bg-pink mt-3 active:scale-95 mt-0',
                    {
                      'text-white bg-greyish cursor-default':
                        isSubmiitting || isExpired,
                    }
                  )}
                  disabled={isSubmiitting || isPollingActive || isExpired}>
                  {isSubmiitting ? (
                    <Spinner className="fill-white" />
                  ) : isExpired ? (
                    <p className="font-[600]">Session Expired</p>
                  ) : (
                    <p className="font-[600]">Pay</p>
                  )}
                </Button>
              </div>
              <div className="pb-4 flex flex-col items-center w-full flex-1 justify-end lg:justify-start">
                <Footer />
              </div>
            </>
          </div>
        )}
        {paymentStatus !== 'form' && (
          <>
            {paymentStatus === 'processing' && showCardModal && (
              <CardPaymentInline
                isVisible={showCardModal}
                reference={reference || ''}
                transactionResponse={transactionResponse}
              />
            )}
            {paymentStatus === 'processing' && paymentMethod === 'mpesa' && (
              <ProcessingPayment />
            )}
            {paymentStatus === 'success' && <PaymentSuccess />}
            {paymentStatus === 'failed' && (
              <PaymentFailure transactionResponse={transactionResponse} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentPageV2;
